import React, { useEffect, useState } from 'react';
import './FallingAnimation.css';

function FallingAnimation({ type = 'snow', enabled = true, count = 25, speed = 'medium' }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      return;
    }

    // Generate falling items with realistic parameters
    const generateItems = () => {
      // Use provided count
      const itemCount = count;
      const newItems = [];

      // Speed multipliers
      const speedMultipliers = {
        slow: 1.5,    // 50% slower
        medium: 1.0,  // Normal speed
        fast: 0.6     // 40% faster
      };
      const speedMultiplier = speedMultipliers[speed] || 1.0;

      for (let i = 0; i < itemCount; i++) {
        // Base speed ranges for each type (adjusted by speed multiplier)
        // Snow: 8-15 seconds (slower, lighter)
        // Leaves: 6-12 seconds (medium speed, heavier than snow but still slow)
        // Valentines: 5-10 seconds (playful, medium-fast)
        const baseDuration = type === 'snow'
          ? Math.random() * 7 + 8    // 8-15 seconds
          : type === 'valentines'
          ? Math.random() * 5 + 5    // 5-10 seconds
          : Math.random() * 6 + 6;   // 6-12 seconds

        const duration = baseDuration * speedMultiplier;

        // Realistic horizontal drift
        // Snow: gentle drift (30-80px)
        // Leaves: more pronounced sway (50-120px)
        // Valentines: bouncy sway (40-90px)
        const swayAmount = type === 'snow'
          ? Math.random() * 50 + 30  // 30-80px
          : type === 'valentines'
          ? Math.random() * 50 + 40  // 40-90px
          : Math.random() * 70 + 50; // 50-120px

        const swayDirection = Math.random() > 0.5 ? 1 : -1;

        newItems.push({
          id: i,
          left: Math.random() * 100, // Random horizontal position (%)
          animationDuration: duration,
          animationDelay: Math.random() * 8, // Stagger start times more
          size: Math.random() * (type === 'snow' ? 8 : type === 'valentines' ? 12 : 15) + (type === 'snow' ? 8 : type === 'valentines' ? 18 : 20),
          opacity: Math.random() * 0.5 + 0.5, // 0.5-1.0 opacity (more visible)
          sway: swayAmount * swayDirection,
          // Add rotation for more realism
          rotationSpeed: type === 'snow'
            ? Math.random() * 2 + 1    // Slow gentle rotation for snow
            : type === 'valentines'
            ? Math.random() * 2 + 2    // Playful rotation
            : Math.random() * 3 + 2,   // Faster tumbling for leaves
          // Add wave motion (zigzag)
          waveAmplitude: type === 'snow'
            ? Math.random() * 20 + 10  // Gentle zigzag
            : type === 'valentines'
            ? Math.random() * 25 + 15  // Fun bouncy motion
            : Math.random() * 30 + 20, // More pronounced wave
        });
      }

      setItems(newItems);
    };

    generateItems();
  }, [type, enabled, count, speed]);

  if (!enabled || items.length === 0) {
    return null;
  }

  const getItemClassName = () => {
    if (type === 'snow') return 'snowflake';
    if (type === 'valentines') return 'valentine';
    return 'leaf';
  };

  const getItemEmoji = (index) => {
    if (type === 'snow') return '❄';
    if (type === 'valentines') {
      // Mix eggplants with hearts for variety
      const emojis = ['🍆', '💕', '🍆', '❤️', '🍆'];
      return emojis[index % emojis.length];
    }
    return '🍂';
  };

  return (
    <div className="falling-animation-container">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`falling-item ${getItemClassName()}`}
          style={{
            left: `${item.left}%`,
            animationDuration: `${item.animationDuration}s`,
            animationDelay: `${item.animationDelay}s`,
            fontSize: `${item.size}px`,
            opacity: item.opacity,
            '--sway': `${item.sway}px`,
            '--rotation-speed': `${item.rotationSpeed}s`,
            '--wave-amplitude': `${item.waveAmplitude}px`,
          }}
        >
          {getItemEmoji(index)}
        </div>
      ))}
    </div>
  );
}

export default FallingAnimation;
